/**
 * Data Validation and Integrity Utilities
 * Provides comprehensive validation for merged data to ensure consistency and reliability
 */

import type { 
  EnhancedMessageTrackingData, 
  CampaignStats,
  MessageTrackingData 
} from '../services/outreach/types';

// Validation configuration
const VALIDATION_CONFIG = {
  // Maximum allowed time difference between related events (in ms)
  MAX_EVENT_TIME_DIFF: 24 * 60 * 60 * 1000, // 24 hours
  // Required fields for different data types
  REQUIRED_TRACKING_FIELDS: ['id', 'recipient', 'timestamp', 'status'] as const,
  REQUIRED_STATS_FIELDS: ['campaignId', 'totalSent'] as const,
  // Valid status values
  VALID_STATUSES: ['sent', 'delivered', 'opened', 'clicked', 'failed', 'pending'] as const,
  // Email regex pattern
  EMAIL_PATTERN: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
} as const;

// Validation result types
export interface ValidationResult {
  isValid: boolean;
  errors: ValidationError[];
  warnings: ValidationWarning[];
  summary: {
    totalItems: number;
    validItems: number;
    errorCount: number;
    warningCount: number;
  };
}

export interface ValidationError {
  type: 'missing_field' | 'invalid_format' | 'logical_inconsistency' | 'duplicate' | 'constraint_violation';
  field?: string;
  itemId?: string;
  message: string;
  severity: 'critical' | 'high' | 'medium';
}

export interface ValidationWarning {
  type: 'suspicious_data' | 'performance_concern' | 'data_quality' | 'timestamp_anomaly';
  field?: string;
  itemId?: string;
  message: string;
  suggestion?: string;
}

/**
 * Validate enhanced message tracking data
 */
export const validateTrackingData = (
  data: EnhancedMessageTrackingData[]
): ValidationResult => {
  const errors: ValidationError[] = [];
  const warnings: ValidationWarning[] = [];
  const seenIds = new Set<string>();
  const seenRecipients = new Map<string, string[]>(); // recipient -> message IDs

  for (const item of data) {
    const itemId = item.mailgunMessageId || item.id;

    // Check required fields
    for (const field of VALIDATION_CONFIG.REQUIRED_TRACKING_FIELDS) {
      if (!item[field]) {
        errors.push({
          type: 'missing_field',
          field,
          itemId,
          message: `Missing required field: ${field}`,
          severity: 'critical',
        });
      }
    }

    // Check for duplicate IDs
    if (seenIds.has(itemId)) {
      errors.push({
        type: 'duplicate',
        field: 'id',
        itemId,
        message: `Duplicate message ID: ${itemId}`,
        severity: 'high',
      });
    }
    seenIds.add(itemId);

    // Validate email format
    if (item.recipient && !VALIDATION_CONFIG.EMAIL_PATTERN.test(item.recipient)) {
      errors.push({
        type: 'invalid_format',
        field: 'recipient',
        itemId,
        message: `Invalid email format: ${item.recipient}`,
        severity: 'medium',
      });
    }

    // Validate status
    if (item.status && !VALIDATION_CONFIG.VALID_STATUSES.includes(item.status as any)) {
      errors.push({
        type: 'invalid_format',
        field: 'status',
        itemId,
        message: `Invalid status: ${item.status}`,
        severity: 'medium',
      });
    }

    // Track recipients for duplicate detection
    if (item.recipient) {
      if (!seenRecipients.has(item.recipient)) {
        seenRecipients.set(item.recipient, []);
      }
      seenRecipients.get(item.recipient)!.push(itemId);
    }

    // Validate timestamp consistency
    validateTimestampConsistency(item, errors, warnings, itemId);

    // Validate status progression logic
    validateStatusProgression(item, errors, warnings, itemId);

    // Check for suspicious data patterns
    checkSuspiciousPatterns(item, warnings, itemId);
  }

  // Check for duplicate recipients (potential data quality issue)
  for (const [recipient, messageIds] of Array.from(seenRecipients)) {
    if (messageIds.length > 1) {
      warnings.push({
        type: 'data_quality',
        field: 'recipient',
        message: `Recipient ${recipient} has multiple messages: ${messageIds.join(', ')}`,
        suggestion: 'Verify if this is intentional (e.g., follow-up emails) or a data duplication issue',
      });
    }
  }

  return {
    isValid: errors.length === 0,
    errors,
    warnings,
    summary: {
      totalItems: data.length,
      validItems: data.length - errors.filter(e => e.severity === 'critical').length,
      errorCount: errors.length,
      warningCount: warnings.length,
    },
  };
};

/**
 * Validate timestamp consistency within a tracking item
 */
const validateTimestampConsistency = (
  item: EnhancedMessageTrackingData,
  errors: ValidationError[],
  warnings: ValidationWarning[],
  itemId: string
): void => {
  const timestamps = {
    sent: item.timestamp,
    delivered: item.deliveredAt,
    opened: item.openedAt,
    clicked: item.clickedAt,
    bounced: item.bouncedAt,
    complained: item.complainedAt,
    unsubscribed: item.unsubscribedAt,
  };

  const validTimestamps = Object.entries(timestamps)
    .filter(([, timestamp]) => timestamp)
    .map(([event, timestamp]) => ({ event, time: new Date(timestamp!).getTime() }))
    .sort((a, b) => a.time - b.time);

  // Check logical order
  for (let i = 1; i < validTimestamps.length; i++) {
    const prev = validTimestamps[i - 1];
    const current = validTimestamps[i];

    // Delivered should come after sent
    if (prev.event === 'sent' && current.event === 'delivered') {
      continue; // This is correct order
    }

    // Opened should come after delivered (if delivered exists)
    if (prev.event === 'delivered' && current.event === 'opened') {
      continue; // This is correct order
    }

    // Clicked should come after opened (if opened exists)
    if (prev.event === 'opened' && current.event === 'clicked') {
      continue; // This is correct order
    }

    // Check for impossible sequences
    if (current.event === 'delivered' && prev.event === 'opened') {
      errors.push({
        type: 'logical_inconsistency',
        field: 'timestamps',
        itemId,
        message: `Email was opened before being delivered`,
        severity: 'high',
      });
    }

    // Check for very large time gaps
    const timeDiff = current.time - prev.time;
    if (timeDiff > VALIDATION_CONFIG.MAX_EVENT_TIME_DIFF) {
      warnings.push({
        type: 'timestamp_anomaly',
        field: 'timestamps',
        itemId,
        message: `Large time gap between ${prev.event} and ${current.event}: ${Math.round(timeDiff / (60 * 60 * 1000))} hours`,
        suggestion: 'Verify if this time gap is realistic for email delivery and engagement',
      });
    }
  }
};

/**
 * Validate status progression logic
 */
const validateStatusProgression = (
  item: EnhancedMessageTrackingData,
  errors: ValidationError[],
  warnings: ValidationWarning[],
  itemId: string
): void => {
  const { status, deliveredAt, openedAt, clickedAt, bouncedAt } = item;

  // Status should match available timestamps
  if (status === 'delivered' && !deliveredAt) {
    errors.push({
      type: 'logical_inconsistency',
      field: 'status',
      itemId,
      message: `Status is 'delivered' but no deliveredAt timestamp`,
      severity: 'medium',
    });
  }

  if (status === 'opened' && !openedAt) {
    errors.push({
      type: 'logical_inconsistency',
      field: 'status',
      itemId,
      message: `Status is 'opened' but no openedAt timestamp`,
      severity: 'medium',
    });
  }

  if (status === 'clicked' && !clickedAt) {
    errors.push({
      type: 'logical_inconsistency',
      field: 'status',
      itemId,
      message: `Status is 'clicked' but no clickedAt timestamp`,
      severity: 'medium',
    });
  }

  // Can't be both delivered and bounced
  if (deliveredAt && bouncedAt) {
    errors.push({
      type: 'logical_inconsistency',
      field: 'status',
      itemId,
      message: `Email cannot be both delivered and bounced`,
      severity: 'high',
    });
  }

  // Opened implies delivered
  if (openedAt && !deliveredAt && status !== 'failed') {
    warnings.push({
      type: 'suspicious_data',
      field: 'status',
      itemId,
      message: `Email was opened but no delivery timestamp`,
      suggestion: 'Verify if delivery tracking is working correctly',
    });
  }
};

/**
 * Check for suspicious data patterns
 */
const checkSuspiciousPatterns = (
  item: EnhancedMessageTrackingData,
  warnings: ValidationWarning[],
  itemId: string
): void => {
  // Check for very fast engagement (opened/clicked within seconds of delivery)
  if (item.deliveredAt && item.openedAt) {
    const deliveryTime = new Date(item.deliveredAt).getTime();
    const openTime = new Date(item.openedAt).getTime();
    const timeDiff = openTime - deliveryTime;

    if (timeDiff < 1000) { // Less than 1 second
      warnings.push({
        type: 'suspicious_data',
        field: 'openedAt',
        itemId,
        message: `Email opened less than 1 second after delivery`,
        suggestion: 'This could indicate automated email scanning or bot activity',
      });
    }
  }

  // Check for missing user agent on opened emails
  if (item.openedAt && !item.userAgent) {
    warnings.push({
      type: 'data_quality',
      field: 'userAgent',
      itemId,
      message: `Email was opened but no user agent information`,
      suggestion: 'Consider enabling user agent tracking for better analytics',
    });
  }

  // Check for geolocation data quality
  if (item.geolocation && (!item.geolocation.country || item.geolocation.country === 'Unknown')) {
    warnings.push({
      type: 'data_quality',
      field: 'geolocation',
      itemId,
      message: `Geolocation data is incomplete or unknown`,
      suggestion: 'Verify geolocation service configuration',
    });
  }
};

/**
 * Validate campaign statistics
 */
export const validateCampaignStats = (stats: CampaignStats): ValidationResult => {
  const errors: ValidationError[] = [];
  const warnings: ValidationWarning[] = [];

  // Check required fields
  for (const field of VALIDATION_CONFIG.REQUIRED_STATS_FIELDS) {
    if (stats[field] === undefined || stats[field] === null) {
      errors.push({
        type: 'missing_field',
        field,
        message: `Missing required field: ${field}`,
        severity: 'critical',
      });
    }
  }

  // Validate numeric consistency
  const { totalSent, delivered, opened, clicked, bounced, complained, unsubscribed } = stats;

  if (totalSent < 0) {
    errors.push({
      type: 'constraint_violation',
      field: 'totalSent',
      message: `Total sent cannot be negative: ${totalSent}`,
      severity: 'high',
    });
  }

  // Sum of outcomes should not exceed total sent
  const totalOutcomes = delivered + bounced;
  if (totalOutcomes > totalSent) {
    errors.push({
      type: 'logical_inconsistency',
      field: 'totals',
      message: `Sum of delivered (${delivered}) and bounced (${bounced}) exceeds total sent (${totalSent})`,
      severity: 'high',
    });
  }

  // Engagement should not exceed delivered
  if (opened > delivered) {
    errors.push({
      type: 'logical_inconsistency',
      field: 'opened',
      message: `Opened count (${opened}) exceeds delivered count (${delivered})`,
      severity: 'high',
    });
  }

  if (clicked > opened) {
    warnings.push({
      type: 'suspicious_data',
      field: 'clicked',
      message: `Clicked count (${clicked}) exceeds opened count (${opened})`,
      suggestion: 'This could indicate click tracking without open tracking, or bot activity',
    });
  }

  // Validate rates
  if (stats.deliveryRate < 0 || stats.deliveryRate > 100) {
    errors.push({
      type: 'constraint_violation',
      field: 'deliveryRate',
      message: `Delivery rate must be between 0 and 100: ${stats.deliveryRate}`,
      severity: 'medium',
    });
  }

  // Check for suspiciously high rates
  if (stats.openRate > 80) {
    warnings.push({
      type: 'suspicious_data',
      field: 'openRate',
      message: `Very high open rate: ${stats.openRate}%`,
      suggestion: 'Verify if this is realistic for your audience and content',
    });
  }

  if (stats.clickRate > 50) {
    warnings.push({
      type: 'suspicious_data',
      field: 'clickRate',
      message: `Very high click rate: ${stats.clickRate}%`,
      suggestion: 'Verify if this is realistic for your content and call-to-action',
    });
  }

  return {
    isValid: errors.length === 0,
    errors,
    warnings,
    summary: {
      totalItems: 1,
      validItems: errors.length === 0 ? 1 : 0,
      errorCount: errors.length,
      warningCount: warnings.length,
    },
  };
};

/**
 * Validate data consistency across multiple campaigns
 */
export const validateCrossDataConsistency = (
  trackingData: Record<string, EnhancedMessageTrackingData[]>,
  campaignStats: Record<string, CampaignStats>
): ValidationResult => {
  const errors: ValidationError[] = [];
  const warnings: ValidationWarning[] = [];

  for (const [campaignId, tracking] of Object.entries(trackingData)) {
    const stats = campaignStats[campaignId];
    
    if (!stats) {
      warnings.push({
        type: 'data_quality',
        message: `Campaign ${campaignId} has tracking data but no statistics`,
        suggestion: 'Generate campaign statistics for complete data consistency',
      });
      continue;
    }

    // Compare tracking data count with stats
    const trackingCount = tracking.length;
    if (trackingCount !== stats.totalSent) {
      warnings.push({
        type: 'data_quality',
        message: `Campaign ${campaignId}: tracking data count (${trackingCount}) doesn't match stats total sent (${stats.totalSent})`,
        suggestion: 'Verify data synchronization between tracking and statistics',
      });
    }

    // Validate status distribution
    const statusCounts = tracking.reduce((acc, item) => {
      acc[item.status] = (acc[item.status] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    if (statusCounts.delivered !== stats.delivered) {
      warnings.push({
        type: 'data_quality',
        message: `Campaign ${campaignId}: delivered count mismatch between tracking (${statusCounts.delivered || 0}) and stats (${stats.delivered})`,
        suggestion: 'Check data synchronization processes',
      });
    }
  }

  return {
    isValid: errors.length === 0,
    errors,
    warnings,
    summary: {
      totalItems: Object.keys(trackingData).length,
      validItems: Object.keys(trackingData).length - errors.length,
      errorCount: errors.length,
      warningCount: warnings.length,
    },
  };
};

/**
 * Create a validation report in human-readable format
 */
export const createValidationReport = (result: ValidationResult): string => {
  const lines: string[] = [];
  
  lines.push('=== Data Validation Report ===');
  lines.push(`Status: ${result.isValid ? '✅ VALID' : '❌ INVALID'}`);
  lines.push(`Total Items: ${result.summary.totalItems}`);
  lines.push(`Valid Items: ${result.summary.validItems}`);
  lines.push(`Errors: ${result.summary.errorCount}`);
  lines.push(`Warnings: ${result.summary.warningCount}`);
  lines.push('');

  if (result.errors.length > 0) {
    lines.push('🚨 ERRORS:');
    result.errors.forEach((error, index) => {
      lines.push(`${index + 1}. [${error.severity.toUpperCase()}] ${error.message}`);
      if (error.field) lines.push(`   Field: ${error.field}`);
      if (error.itemId) lines.push(`   Item: ${error.itemId}`);
      lines.push('');
    });
  }

  if (result.warnings.length > 0) {
    lines.push('⚠️  WARNINGS:');
    result.warnings.forEach((warning, index) => {
      lines.push(`${index + 1}. ${warning.message}`);
      if (warning.field) lines.push(`   Field: ${warning.field}`);
      if (warning.itemId) lines.push(`   Item: ${warning.itemId}`);
      if (warning.suggestion) lines.push(`   Suggestion: ${warning.suggestion}`);
      lines.push('');
    });
  }

  return lines.join('\n');
};